import React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import { Delete } from "@mui/icons-material";
import DateTimeRangePicker from "../../components/FormElements/DateTimeRangePicker";
import CustomTextField from "../../components/FormElements/CustomTextField";
import AlternativeNamesFields from "../../components/FormElements/AlternativeNamesFields";
import { Upload } from "antd";
import { ImageIcon, X } from "lucide-react";
import { labelStyles, MAX_STICKERS } from "./helpers";
import ThemeStickerPicker from "../../components/ThemeStickerPicker";
import FeedGroupSizeField from "../../components/FormElements/FeedGroupSizeField";

const { Dragger } = Upload;

const ChallengeFormModal = ({
  open,
  onClose,
  classes,
  isEditMode,
  isAdmin,
  challengeTitle,
  setChallengeTitle,
  alternativeNames,
  setAlternativeNames,
  challengeCategory,
  categories,
  handleCategoryChange,
  challengeStartDate,
  setChallengeStartDate,
  challengeEndDate,
  setChallengeEndDate,
  challengeDateTbd,
  setChallengeDateTbd,
  challengeBackgroundPreviewUrl,
  setBackgroundFileData,
  editingChallenge,
  setEditingChallenge,
  handleBackgroundFileUpload,
  stickersAllowed,
  feedGroupSizeAllowed,
  challengeFeedGroupSize,
  setChallengeFeedGroupSize,
  stickers,
  handleStickerUpload,
  handleAddLibrarySticker,
  handleRemoveSticker,
  handleClearStickers,
  challengeIsPromoted,
  setChallengeIsPromoted,
  editIndex,
  challenges,
  handleDeleteChallenge,
  handleUpdateChallenge,
  handleAddChallenge,
}) => {
  return (
    <Dialog
      sx={{
        zIndex: 9,
        "& .MuiDialog-container .MuiPaper-elevation": {
          borderRadius: "8px",
          width: "480px",
        },
      }}
      open={open}
      onClose={onClose}
    >
      <DialogTitle
        sx={{
          padding: "30px 0 15px",
          textAlign: "center",
          fontWeight: 700,
        }}
      >
        {isEditMode ? "Edit Challenge" : "Add Challenge"}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box mt={1}>
          <CustomTextField
            label="Title of the challenge"
            value={challengeTitle}
            onChange={(e) => setChallengeTitle(e.target.value)}
            inputProps={{ maxLength: 50 }}
          />
          <Typography variant="caption" sx={{ color: "#555", display: "block", mt: 0.5 }}>
            Default title. Shown when a language has no alternative name.
          </Typography>
          <AlternativeNamesFields
            key={editingChallenge?._id || "new"}
            value={alternativeNames}
            onChange={setAlternativeNames}
            maxLength={50}
            onEnglishTitleChange={setChallengeTitle}
          />
        </Box>
        <FormControl sx={{ marginTop: "16px" }} fullWidth margin="normal">
          <InputLabel
            id="category-select-label"
            sx={{
              color: "gray",
              "& .Mui-focused": {
                color: "#142C5C !important",
              },
            }}
          >
            Choose Category
          </InputLabel>
          <Select
            labelId="category-select-label"
            id="category-select"
            value={challengeCategory}
            onChange={handleCategoryChange}
            displayEmpty
            label="Choose Category"
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  width: 250,
                },
              },
            }}
            sx={{
              height: "55px",
              "& .MuiSelect-select": {
                padding: "8px 14px",
                fontSize: "14px",
                color: "black",
                backgroundColor: isEditMode ? "#e0e0e0" : "white",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: "4px",
              },
              "& .MuiSelect-select.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "black",
              },
            }}
            disabled={isEditMode}
          >
            {categories.map((category) => {
              const isSelected = category._id === challengeCategory;

              return (
                <MenuItem
                  key={category._id}
                  value={category._id}
                  selected={isSelected}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: "#e0e0e0",
                    },
                    backgroundColor: isEditMode ? "#e0e0e0" : "white",
                    color: isEditMode ? "#a0a0a0" : "black",
                    "& .MuiTypography-root": {
                      color: isEditMode ? "#a0a0a0" : "black",
                    },
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {category.logo && (
                      <img
                        src={category.logo}
                        alt={category.name}
                        style={{
                          width: "24px",
                          height: "24px",
                          marginRight: "8px",
                          filter: isEditMode ? "grayscale(100%)" : "none",
                          opacity: isEditMode ? "0.6" : "1",
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div>{category.name}</div>
                      {category.type && (
                        <span
                          style={{ fontSize: "0.875rem", color: "#D3D3D3" }}
                        >
                          ({category.type})
                        </span>
                      )}
                    </div>
                  </div>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(challengeDateTbd)}
                onChange={(e) => {
                  setChallengeDateTbd(e.target.checked);
                  if (e.target.checked) {
                    setChallengeStartDate(null);
                    setChallengeEndDate(null);
                  }
                }}
              />
            }
            label={
              <span style={{ fontSize: "14px" }}>
                Schedule not decided yet (TBD)
                <span
                  style={{ fontSize: "11px", color: "gray", display: "block" }}
                >
                  Challenge stays hidden from users until dates are set
                </span>
              </span>
            }
          />
        </Box>
        <DateTimeRangePicker
          startDate={challengeStartDate}
          endDate={challengeEndDate}
          onStartDateChange={setChallengeStartDate}
          onEndDateChange={setChallengeEndDate}
          challengeCategory={challengeCategory}
          disabled={Boolean(challengeDateTbd)}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: "#555", fontWeight: 600 }}>
            Duration:
          </Typography>
          {[
            { label: "24 Hours", hours: 24 },
            { label: "48 Hours", hours: 48 },
            { label: "72 Hours", hours: 72 },
          ].map((preset) => (
            <Button
              key={preset.hours}
              size="small"
              variant="outlined"
              disabled={!challengeStartDate}
              onClick={() => {
                const start = dayjs(challengeStartDate);
                if (start.isValid()) {
                  setChallengeEndDate(start.add(preset.hours, "hour"));
                }
              }}
              sx={{
                minWidth: 0,
                px: 1.5,
                py: 0.25,
                fontSize: "12px",
                textTransform: "none",
                borderColor:
                  challengeStartDate &&
                  challengeEndDate &&
                  dayjs(challengeEndDate).diff(
                    dayjs(challengeStartDate),
                    "hour"
                  ) === preset.hours
                    ? "#142C5C"
                    : "#bbb",
                color:
                  challengeStartDate &&
                  challengeEndDate &&
                  dayjs(challengeEndDate).diff(
                    dayjs(challengeStartDate),
                    "hour"
                  ) === preset.hours
                    ? "#fff"
                    : "#333",
                backgroundColor:
                  challengeStartDate &&
                  challengeEndDate &&
                  dayjs(challengeEndDate).diff(
                    dayjs(challengeStartDate),
                    "hour"
                  ) === preset.hours
                    ? "#142C5C"
                    : "transparent",
                "&:hover": {
                  borderColor: "#142C5C",
                  color: "#142C5C",
                  backgroundColor: "rgba(20, 44, 92, 0.04)",
                },
                "&.Mui-disabled": {
                  borderColor: "#ddd",
                  color: "#bbb",
                },
              }}
            >
              {preset.label}
            </Button>
          ))}
        </Box>
        <Box mt={2} mb={1}>
          <label style={{ ...labelStyles, top: "-9px" }}>
            Background Image
          </label>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Recommended: landscape image, at least 1080×720 (3:2 aspect ratio).
          </Typography>
          {challengeBackgroundPreviewUrl ? (
            <Box className="contest-upload-preview contest-upload-preview--wide">
              <img src={challengeBackgroundPreviewUrl} alt="background" />
              <button
                className="contest-upload-remove"
                onClick={() => {
                  setBackgroundFileData(null);
                  if (editingChallenge) {
                    setEditingChallenge({
                      ...editingChallenge,
                      backgroundImage: null,
                      backgroundImageId: null,
                    });
                  }
                }}
                type="button"
              >
                <X size={14} />
              </button>
            </Box>
          ) : (
            <Dragger
              showUploadList={false}
              customRequest={handleBackgroundFileUpload}
              accept="image/*"
              className="contest-upload-dragger"
            >
              <Box className="contest-upload-dragger-content">
                <ImageIcon size={32} strokeWidth={1.5} color="#667eea" />
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                  Drag & drop background image here
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  or click to browse (JPG, PNG, max 5MB)
                </Typography>
              </Box>
            </Dragger>
          )}
          {challengeBackgroundPreviewUrl && (
            <Box sx={{ mt: 1.5 }}>
              <Dragger
                showUploadList={false}
                customRequest={handleBackgroundFileUpload}
                accept="image/*"
                className="contest-upload-dragger"
              >
                <Box className="contest-upload-dragger-content">
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Replace background image
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Drag & drop or click to upload a new image
                  </Typography>
                </Box>
              </Dragger>
            </Box>
          )}
        </Box>
        {stickersAllowed && (
          <Box mt={2} mb={1}>
            <label style={{ ...labelStyles, top: "-9px" }}>
              Stickers (up to {MAX_STICKERS})
            </label>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Shown on the recording edit screen for this challenge. PNG
              keeps transparency. Saving replaces the whole set.
            </Typography>
            {stickers.length > 0 && (
              <Box className="sticker-preview-row">
                {stickers.map((sticker, index) => (
                  <Box
                    key={sticker.mediaId || sticker.url}
                    className="contest-upload-preview contest-upload-preview--sticker"
                  >
                    <img src={sticker.url} alt={`sticker ${index + 1}`} />
                    <button
                      className="contest-upload-remove"
                      onClick={() => handleRemoveSticker(index)}
                      type="button"
                    >
                      <X size={14} />
                    </button>
                  </Box>
                ))}
              </Box>
            )}
            {stickers.length < MAX_STICKERS && (
              <Box sx={{ mt: stickers.length ? 1.5 : 0 }}>
                <Dragger
                  multiple
                  showUploadList={false}
                  customRequest={handleStickerUpload}
                  accept="image/*"
                  className="contest-upload-dragger"
                >
                  <Box className="contest-upload-dragger-content">
                    <ImageIcon size={32} strokeWidth={1.5} color="#667eea" />
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, fontWeight: 500 }}
                    >
                      Drag & drop stickers here
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      or click to browse ({stickers.length} of{" "}
                      {MAX_STICKERS} added, PNG/JPG, max 5MB)
                    </Typography>
                  </Box>
                </Dragger>
              </Box>
            )}
            <ThemeStickerPicker
              stickers={stickers}
              maxStickers={MAX_STICKERS}
              onPick={handleAddLibrarySticker}
            />
            {stickers.length > 0 && (
              <Button
                size="small"
                onClick={handleClearStickers}
                sx={{
                  mt: 1,
                  textTransform: "none",
                  color: "#d32f2f",
                  padding: 0,
                  minWidth: 0,
                }}
              >
                Clear all stickers
              </Button>
            )}
          </Box>
        )}
        {feedGroupSizeAllowed && (
          <FeedGroupSizeField
            value={challengeFeedGroupSize}
            onChange={setChallengeFeedGroupSize}
            defaultLabel="Default (group by category)"
            hint="How many pagents of this challenge appear back-to-back on the voting feed. Leave this on the default to keep the usual category grouping."
          />
        )}
        <Tooltip
          title="Only one challenge can be promoted per category at a time"
          arrow
          placement="right"
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={challengeIsPromoted}
                onChange={(e) => setChallengeIsPromoted(e.target.checked)}
                disabled={
                  !challengeStartDate ||
                  !challengeEndDate ||
                  dayjs().isBefore(challengeStartDate) ||
                  dayjs().isAfter(challengeEndDate)
                }
              />
            }
            label={
              <span>
                {challengeIsPromoted
                  ? "Currently Promoted ⭐"
                  : "Promote this challenge ⭐"}
                {challengeStartDate && challengeEndDate && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      display: "block",
                    }}
                  >
                    {dayjs().isBefore(challengeStartDate)
                      ? "Challenge hasn't started yet"
                      : dayjs().isAfter(challengeEndDate)
                      ? "Challenge has ended"
                      : challengeIsPromoted
                      ? "Click to unpromote"
                      : ""}
                  </span>
                )}
              </span>
            }
          />
        </Tooltip>
        <Box
          className={classes.actionsContainer}
          sx={{
            justifyContent:
              isEditMode && isAdmin ? "space-between" : "flex-end",
          }}
        >
          {/* Delete button - only show in edit mode for admins */}
          {isEditMode && isAdmin && (
            <Button
              startIcon={<Delete />}
              sx={{
                border: "1px solid #d32f2f",
                color: "#d32f2f",
                borderRadius: "6px",
                fontSize: "14px",
                "&:hover": {
                  backgroundColor: "#ffebee",
                  borderColor: "#b71c1c",
                  color: "#b71c1c",
                },
              }}
              onClick={() => {
                if (editIndex !== null && challenges[editIndex]) {
                  onClose();
                  handleDeleteChallenge(challenges[editIndex]);
                }
              }}
            >
              Delete
            </Button>
          )}
          <Box>
            <Button
              sx={{
                border: "1px solid #888",
                color: "#888",
                borderRadius: "6px",
                fontSize: "14px",
                marginRight: "15px",
                "&:hover": {
                  backgroundColor: "#b9b9b9",
                  color: "#222",
                  borderColor: "#b9b9b9",
                },
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              className={classes.button}
              onClick={
                isEditMode ? handleUpdateChallenge : handleAddChallenge
              }
            >
              {isEditMode ? "Update Challenge" : "Add Challenge"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChallengeFormModal;
